import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Download, Upload, CheckCircle, AlertCircle, Trash2,
  ExternalLink, Loader2, FileCheck2, Paperclip, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const KAT_LABEL = {
  FB: "Formblätter (FB)",
  NW: "Nachweise (NW)",
  VA: "Verfahrensanweisungen (VA)",
  STD: "Stammdaten (STD)",
};

const STATUS_INFO = {
  generator: {
    label: "Generator",
    badge: "bg-emerald-100 text-emerald-800 border-emerald-300",
    icon: CheckCircle,
    desc: "Wird vom CRM live generiert",
  },
  blanko_uploaded: {
    label: "Blanko vorhanden",
    badge: "bg-blue-100 text-blue-800 border-blue-300",
    icon: FileCheck2,
    desc: "Blanko-Vorlage hochgeladen",
  },
  fehlt: {
    label: "Fehlt",
    badge: "bg-amber-100 text-amber-800 border-amber-300",
    icon: AlertCircle,
    desc: "Vorlage muss noch hochgeladen werden",
  },
};

// Sekundäres Badge: zeigt ob die hochgeladene Blanko-Vorlage ein automatisch
// generierter Skeleton-Platzhalter ist oder eine final freigegebene Version
// vom QMB. Wichtig für Audit-Tracking.
const SOURCE_INFO = {
  skeleton: {
    label: "Skeleton",
    badge: "bg-amber-50 text-amber-700 border-amber-200",
    title: "Automatisch generierter Platzhalter — bitte durch finales Dokument vom QMB ersetzen",
  },
  final: {
    label: "Final",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
    title: "Final freigegebene Vorlage (vom QMB hochgeladen)",
  },
};

const QMDokumente = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [docs, setDocs] = useState([]);
  const [stats, setStats] = useState({ total: 0, mit_generator: 0, mit_blanko_pdf: 0, fehlt: 0 });
  const [kategorienFilter, setKategorienFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [uploadingCode, setUploadingCode] = useState(null);
  const [generatingCode, setGeneratingCode] = useState(null);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [refsModalCode, setRefsModalCode] = useState(null);

  const downloadReference = async (code, refId, fallbackName) => {
    try {
      const res = await axios.get(`${API}/qm/documents/${code}/references/${refId}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = (fallbackName || `${code}_${refId}`).replace(/\.[^/.]+$/, "") + ".pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Referenz konnte nicht geladen werden");
    }
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/qm/documents`);
      setDocs(res.data.documents || []);
      setStats(res.data.stats || { total: 0 });
    } catch (e) {
      toast.error("QM-Dokumente konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const filtered = docs.filter((d) => {
    if (kategorienFilter !== "all" && d.kategorie !== kategorienFilter) return false;
    if (statusFilter !== "all" && d.status !== statusFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!`${d.code} ${d.name} ${d.anwendung || ""}`.toLowerCase().includes(s)) return false;
    }
    return true;
  });

  const grouped = filtered.reduce((acc, d) => {
    const k = d.kategorie || "Sonstige";
    (acc[k] = acc[k] || []).push(d);
    return acc;
  }, {});

  const downloadBlanko = async (code) => {
    try {
      const res = await axios.get(`${API}/qm/documents/${code}/download-blanko`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${code}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download fehlgeschlagen");
    }
  };

  const openGenerator = (endpoint) => {
    // Generator-Endpoints öffnen einen neuen Tab.
    // {id}-Platzhalter brauchen einen Teilnehmer-Kontext → Hinweis anzeigen.
    if (endpoint.includes("{id}")) {
      toast.info("Dieses Formular wird pro Teilnehmer generiert. Öffne dafür den TN in der Mappe und klicke auf 'PDF generieren'.", { duration: 6000 });
      return;
    }
    axios.get(`${BACKEND_URL}${endpoint}`, { responseType: "blob" })
      .then((res) => window.open(window.URL.createObjectURL(res.data), "_blank"))
      .catch(() => toast.error("Formular konnte nicht erzeugt werden"));
  };

  const uploadBlanko = async (code, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Nur PDF-Dateien erlaubt");
      return;
    }
    setUploadingCode(code);
    const t = toast.loading(`Lade ${file.name} hoch...`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/qm/documents/${code}/upload-blanko`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${code}: Blanko-Vorlage gespeichert`, { id: t });
      load();
    } catch (e) {
      toast.error("Upload fehlgeschlagen: " + (e.response?.data?.detail || e.message), { id: t });
    } finally {
      setUploadingCode(null);
    }
  };

  const deleteBlanko = async (code) => {
    if (!window.confirm(`Blanko-Vorlage für ${code} wirklich entfernen?`)) return;
    try {
      await axios.delete(`${API}/qm/documents/${code}/blanko`);
      toast.success("Vorlage entfernt");
      load();
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const exportListePdf = async () => {
    const t = toast.loading("PDF wird generiert...");
    try {
      const res = await axios.get(`${API}/qm/documents/export-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `NW_QM_03_Dokumentenliste_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Dokumentenliste heruntergeladen", { id: t });
    } catch (e) {
      toast.error("Export fehlgeschlagen", { id: t });
    }
  };

  const reseed = async () => {
    if (!window.confirm("Stammliste neu aus Code übernehmen? Hochgeladene Blanko-PDFs bleiben erhalten.")) return;
    try {
      const r = await axios.post(`${API}/qm/documents/seed?force=true`);
      toast.success(`Aktualisiert: +${r.data.created} neu, ${r.data.updated} überschrieben`);
      load();
    } catch (e) {
      toast.error("Seed fehlgeschlagen");
    }
  };

  const generateSkeleton = async (code) => {
    setGeneratingCode(code);
    const t = toast.loading(`Erstelle Skeleton für ${code}...`);
    try {
      const r = await axios.post(`${API}/qm/documents/${code}/generate-skeleton?save_as_blanko=true`);
      toast.success(`${code}: Skeleton erstellt (${(r.data.size/1024).toFixed(1)} KB)`, { id: t });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Skeleton-Erstellung fehlgeschlagen", { id: t });
    } finally {
      setGeneratingCode(null);
    }
  };

  const bulkGenerateSkeletons = async () => {
    if (!window.confirm("Für ALLE fehlenden Dokumente, für die ein Template hinterlegt ist, eine Skeleton-Vorlage erstellen?")) return;
    setBulkGenerating(true);
    const t = toast.loading("Erstelle alle Skeletons...");
    try {
      const r = await axios.post(`${API}/qm/documents/bulk-generate-skeletons`);
      toast.success(`${r.data.erfolgreich} Skeletons erstellt (${r.data.fehlgeschlagen} fehlgeschlagen)`, { id: t, duration: 8000 });
      load();
    } catch (e) {
      toast.error("Bulk-Generation fehlgeschlagen", { id: t });
    } finally {
      setBulkGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="qm-dokumente-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#4A463D]">
            <FileText size={22} /> QM-Dokumentenliste (NW_QM_03)
          </DialogTitle>
          <DialogDescription>
            Alle Formblätter, Nachweise und Verfahrensanweisungen aus dem QM-Handbuch — mit Status,
            Generator-Verknüpfung und Blanko-Vorlagen.
          </DialogDescription>
        </DialogHeader>

        {/* KPI-Kacheln */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <div className="rounded-lg border p-3 bg-white">
            <div className="text-xs text-stone-500">Gesamt</div>
            <div className="text-2xl font-bold text-stone-800" data-testid="qm-stat-total">{stats.total}</div>
          </div>
          <div className="rounded-lg border p-3 bg-emerald-50 border-emerald-200">
            <div className="text-xs text-emerald-700">Vom CRM generiert</div>
            <div className="text-2xl font-bold text-emerald-800" data-testid="qm-stat-generator">{stats.mit_generator}</div>
          </div>
          <div className="rounded-lg border p-3 bg-blue-50 border-blue-200" title="Finale Vorlagen vom QMB hochgeladen">
            <div className="text-xs text-blue-700">Final-Vorlagen</div>
            <div className="text-2xl font-bold text-blue-800" data-testid="qm-stat-blanko-final">{stats.mit_blanko_final || 0}</div>
          </div>
          <div className="rounded-lg border p-3 bg-amber-50 border-amber-200" title="Automatisch generierte Platzhalter — bitte durch finale Versionen ersetzen">
            <div className="text-xs text-amber-700">Skeleton-Platzhalter</div>
            <div className="text-2xl font-bold text-amber-800" data-testid="qm-stat-blanko-skeleton">{stats.mit_blanko_skeleton || 0}</div>
          </div>
          <div className="rounded-lg border p-3 bg-red-50 border-red-200">
            <div className="text-xs text-red-700">Fehlt noch</div>
            <div className="text-2xl font-bold text-red-800" data-testid="qm-stat-fehlt">{stats.fehlt}</div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap gap-2 mb-4">
          <Input
            placeholder="Suchen (Code / Name / Anwendung)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs"
            data-testid="qm-search-input"
          />
          <Select value={kategorienFilter} onValueChange={setKategorienFilter}>
            <SelectTrigger className="w-44" data-testid="qm-kategorie-filter">
              <SelectValue placeholder="Kategorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Kategorien</SelectItem>
              <SelectItem value="FB">Formblätter (FB)</SelectItem>
              <SelectItem value="NW">Nachweise (NW)</SelectItem>
              <SelectItem value="VA">Verfahrensanweisungen (VA)</SelectItem>
              <SelectItem value="STD">Stammdaten (STD)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="qm-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="generator">Vom CRM generiert</SelectItem>
              <SelectItem value="blanko_uploaded">Blanko hochgeladen</SelectItem>
              <SelectItem value="fehlt">Fehlt noch</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={exportListePdf} data-testid="qm-export-pdf-btn">
            <Download size={14} className="mr-2" />
            Liste als PDF (NW_QM_03)
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={bulkGenerateSkeletons}
            disabled={bulkGenerating}
            className="border-violet-300 text-violet-800 hover:bg-violet-50"
            data-testid="qm-bulk-skeleton-btn"
            title="Erstellt für alle fehlenden Dokumente AZAV-konforme Skeleton-Vorlagen (mit Dickies-Daten, Freigabeblock)"
          >
            {bulkGenerating ? <Loader2 size={14} className="animate-spin mr-2" /> : <FileText size={14} className="mr-2" />}
            Alle Skeletons erstellen
          </Button>
          <Button variant="ghost" size="sm" onClick={reseed} title="Stammliste neu aus Code übernehmen">
            Stammdaten erneuern
          </Button>
        </div>

        {/* Liste */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {Object.keys(grouped).length === 0 && (
              <div className="text-center text-stone-500 py-8">Keine Dokumente passen zum Filter.</div>
            )}
            {["FB", "NW", "VA", "STD"].filter((k) => grouped[k]).map((kat) => (
              <div key={kat}>
                <h3 className="text-sm font-bold text-[#4A463D] mb-2 flex items-center gap-2">
                  {KAT_LABEL[kat] || kat}
                  <Badge variant="secondary">{grouped[kat].length}</Badge>
                </h3>
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-stone-50">
                        <TableHead className="w-24">Code</TableHead>
                        <TableHead>Bezeichnung</TableHead>
                        <TableHead className="w-20">Version</TableHead>
                        <TableHead className="w-20">Stand</TableHead>
                        <TableHead className="w-40">Anwendung</TableHead>
                        <TableHead className="w-32">Status</TableHead>
                        <TableHead className="w-56 text-right">Aktion</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {grouped[kat].map((d) => {
                        const info = STATUS_INFO[d.status] || STATUS_INFO.fehlt;
                        const Icon = info.icon;
                        return (
                          <TableRow key={d.code} data-testid={`qm-row-${d.code}`}>
                            <TableCell className="font-mono text-xs">{d.code}</TableCell>
                            <TableCell className="text-sm">
                              <div>{d.name}</div>
                              {d.is_per_teilnehmer && (
                                <div className="text-xs text-stone-500 mt-0.5">je Teilnehmer individuell</div>
                              )}
                              {Array.isArray(d.referenz_anhaenge) && d.referenz_anhaenge.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setRefsModalCode(d.code)}
                                  className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 transition"
                                  data-testid={`qm-refs-badge-${d.code}`}
                                  title="Audit-Referenzen anzeigen"
                                >
                                  <Paperclip size={11} /> {d.referenz_anhaenge.length} Referenz{d.referenz_anhaenge.length === 1 ? "" : "en"}
                                </button>
                              )}
                            </TableCell>
                            <TableCell className="text-xs">{d.version}</TableCell>
                            <TableCell className="text-xs">{d.datum}</TableCell>
                            <TableCell className="text-xs text-stone-600">{d.anwendung}</TableCell>
                            <TableCell>
                              <div className="flex flex-wrap items-center gap-1">
                                <Badge className={`gap-1 ${info.badge}`} title={info.desc}>
                                  <Icon size={12} />
                                  {info.label}
                                </Badge>
                                {d.status === "blanko_uploaded" && d.blanko_pdf_source && SOURCE_INFO[d.blanko_pdf_source] && (
                                  <Badge
                                    variant="outline"
                                    className={`text-xs ${SOURCE_INFO[d.blanko_pdf_source].badge}`}
                                    title={SOURCE_INFO[d.blanko_pdf_source].title}
                                    data-testid={`qm-source-${d.code}`}
                                  >
                                    {SOURCE_INFO[d.blanko_pdf_source].label}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap justify-end gap-1">
                                {d.status === "generator" && d.generator_endpoint && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-8"
                                    onClick={() => openGenerator(d.generator_endpoint)}
                                    data-testid={`qm-generator-${d.code}`}
                                    title={d.generator_endpoint}
                                  >
                                    <ExternalLink size={12} className="mr-1" />
                                    Öffnen
                                  </Button>
                                )}
                                {d.status === "blanko_uploaded" && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-8"
                                      onClick={() => downloadBlanko(d.code)}
                                      data-testid={`qm-download-${d.code}`}
                                    >
                                      <Download size={12} className="mr-1" />
                                      Download
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 text-rose-700 hover:bg-rose-50"
                                      onClick={() => deleteBlanko(d.code)}
                                      data-testid={`qm-delete-${d.code}`}
                                      title="Blanko-Vorlage entfernen"
                                    >
                                      <Trash2 size={12} />
                                    </Button>
                                  </>
                                )}
                                <label className="cursor-pointer">
                                  <input
                                    type="file"
                                    accept="application/pdf,.pdf"
                                    className="hidden"
                                    onChange={(e) => {
                                      const f = e.target.files?.[0];
                                      if (f) uploadBlanko(d.code, f);
                                      e.target.value = "";
                                    }}
                                    data-testid={`qm-upload-input-${d.code}`}
                                  />
                                  <span
                                    className={`inline-flex items-center text-xs h-8 px-2 rounded border ${
                                      uploadingCode === d.code
                                        ? "bg-stone-100 text-stone-500"
                                        : "bg-white hover:bg-stone-50 text-stone-700 border-stone-300"
                                    }`}
                                  >
                                    {uploadingCode === d.code ? (
                                      <Loader2 size={12} className="animate-spin mr-1" />
                                    ) : (
                                      <Upload size={12} className="mr-1" />
                                    )}
                                    {d.status === "blanko_uploaded" ? "Ersetzen" : "Blanko hochladen"}
                                  </span>
                                </label>
                                {d.status === "fehlt" && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-8 border-violet-300 text-violet-700 hover:bg-violet-50"
                                    onClick={() => generateSkeleton(d.code)}
                                    disabled={generatingCode === d.code}
                                    data-testid={`qm-skeleton-${d.code}`}
                                    title="AZAV-konforme Vorlage automatisch generieren (mit Dickies-Daten + Freigabeblock)"
                                  >
                                    {generatingCode === d.code
                                      ? <Loader2 size={12} className="animate-spin mr-1" />
                                      : <FileText size={12} className="mr-1" />}
                                    Skeleton
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>

      {/* Audit-Referenzen Modal (außerhalb DialogContent, eigener Dialog) */}
      <Dialog open={!!refsModalCode} onOpenChange={(o) => !o && setRefsModalCode(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip size={16} className="text-amber-700" />
              Audit-Referenzen
              <span className="font-mono text-xs text-stone-500 ml-2">{refsModalCode}</span>
            </DialogTitle>
            <DialogDescription>
              Ausgefüllte Originale (Audit-Nachweise) und historische Vorlagen für diesen QM-Code.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {(docs.find((d) => d.code === refsModalCode)?.referenz_anhaenge || []).map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between gap-3 p-3 border border-stone-200 rounded-md hover:bg-stone-50 transition"
                data-testid={`qm-ref-${r.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-stone-900 text-sm truncate">{r.variant || "Standard"}</div>
                  <div className="text-xs text-stone-500 mt-0.5 flex flex-wrap gap-x-3">
                    <span className="font-mono">{r.filename}</span>
                    {r.size && <span>{(r.size/1024).toFixed(0)} KB</span>}
                    {r.uploaded_at && <span>{new Date(r.uploaded_at).toLocaleDateString("de-DE")}</span>}
                  </div>
                  {r.kommentar && (
                    <div className="text-xs text-stone-400 mt-1 italic">{r.kommentar}</div>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadReference(refsModalCode, r.id, r.filename)}
                  data-testid={`qm-ref-download-${r.id}`}
                >
                  <Download size={14} className="mr-1.5" /> PDF
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default QMDokumente;
